import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import type { MenuItemResponse } from "@/api/lib/db/types";
import Icon from "../../../lib/components/custom/Icon";
import { Image } from "../../../lib/components/custom/Image";
import { Button } from "../../../lib/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from "../../../lib/components/ui/dialog";
import { ScrollArea } from "../../../lib/components/ui/scroll-area";
import {
	Tabs,
	TabsContent,
	TabsList,
	TabsTrigger,
} from "../../../lib/components/ui/tabs";
import { useApi } from "../../../lib/hooks/api/use-api";

interface ImageSelectionDialogProps {
	item: MenuItemResponse;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

export function ImageSelectionDialog({
	item,
	open,
	onOpenChange,
	onSuccess,
}: ImageSelectionDialogProps) {
	const api = useApi();
	const [uploading, setUploading] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);

	// Fetch category images
	const { data: categoryImages, isLoading: isLoadingImages } =
		api.menu.getCategoryImages({ category: item.category });

	// Fetch recent images for this category (if item has categoryId)
	const { data: recentImagesData } = api.uploads.getRecentImages({
		usageType: "category_image",
		entityId: item.categoryId || 0,
		limit: 10,
	});

	// Transform recent images data to match expected structure
	const recentImages = React.useMemo(() => {
		if (!recentImagesData?.images) return [];
		return recentImagesData.images.map((img) => ({
			id: img.id,
			imageUrl: img.url,
			name: img.filename, // Use filename as display name
		}));
	}, [recentImagesData]);

	const updateItemMutation = api.menu.updateItem;
	const uploadImageMutation = api.uploads.uploadMenuImage;

	const handleSaveImage = async (imageUrl: string | null) => {
		if (!imageUrl && !selectedFile) return;

		try {
			if (selectedFile) {
				// Upload the selected file
				setUploading(true);
				await uploadImageMutation.mutateAsync({
					menuItemId: item.id,
					image: selectedFile,
				});
				setUploading(false);
			} else if (imageUrl) {
				// Update with selected category image
				await updateItemMutation.mutateAsync({
					id: item.id,
					data: { imageUrl },
				});
			}

			toast.success("Image updated successfully");
			onSuccess();
			onOpenChange(false);
			setSelectedImageUrl(null);
			setSelectedFile(null);
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
				setPreviewUrl(null);
			}
		} catch (error) {
			toast.error("Failed to update image");
			setUploading(false);
		}
	};

	const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		// Create preview URL
		const url = URL.createObjectURL(file);
		setPreviewUrl(url);
		setSelectedFile(file);
		setSelectedImageUrl(null); // Clear category selection when file is selected
	};

	const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

	// Cleanup preview URL when component unmounts
	useEffect(() => {
		return () => {
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [previewUrl]);

	return (
		<Dialog
			open={open}
			onOpenChange={(newOpen) => {
				if (!newOpen) {
					// Cleanup when dialog closes
					setSelectedImageUrl(null);
					setSelectedFile(null);
					if (previewUrl) {
						URL.revokeObjectURL(previewUrl);
						setPreviewUrl(null);
					}
				}
				onOpenChange(newOpen);
			}}
		>
			<DialogContent className="max-w-4xl max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
				<DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between">
					<DialogTitle className="text-xl">
						Update Image for {item.name}
					</DialogTitle>
					{(selectedImageUrl || selectedFile) && (
						<Button
							variant="primary"
							onClick={() => handleSaveImage(selectedImageUrl)}
							disabled={uploading}
						>
							{uploading ? (
								<Icon name="Loader" className="w-4 h-4 mr-2 animate-spin" />
							) : (
								<>
									<Icon name="Check" className="w-4 h-4" />
									Save Changes
								</>
							)}
						</Button>
					)}
				</DialogHeader>

				<Tabs
					defaultValue="category"
					className="flex-1 flex flex-col overflow-hidden"
				>
					<TabsList className="p-8">
						<TabsTrigger value="category">Category Images</TabsTrigger>
						<TabsTrigger value="upload">Upload New</TabsTrigger>
					</TabsList>

					<TabsContent
						value="category"
						className="flex-1 overflow-hidden p-0 m-0"
					>
						<ScrollArea className="h-[60vh]">
							<div className="p-6">
								{isLoadingImages ? (
									<div className="flex items-center justify-center h-40">
										Loading images...
									</div>
								) : categoryImages && categoryImages.length > 0 ? (
									<div className="grid grid-cols-2 md:grid-cols-3 gap-6">
										{categoryImages.map((img: string, i: number) => (
											<div className="relative">
												<Image
													key={i}
													src={img}
													alt={`Category option ${i}`}
													className="w-full h-full object-cover cursor-pointer aspect-square object-center hover:opacity-60 transition-opacity duration-200 rounded-large"
													onClick={() => {
														setSelectedImageUrl(img);
														// Clear file selection when category image is selected
														setSelectedFile(null);
														if (previewUrl) {
															URL.revokeObjectURL(previewUrl);
															setPreviewUrl(null);
														}
													}}
												/>

												{selectedImageUrl === img && (
													<div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px] rounded-large">
														<div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
															<Icon name="Check" className="w-4 h-4" />
															Selected
														</div>
													</div>
												)}
											</div>
										))}
									</div>
								) : null}
							</div>
						</ScrollArea>
					</TabsContent>

					<TabsContent
						value="upload"
						className="flex-1 overflow-hidden p-0 m-0"
					>
						<ScrollArea className="h-[60vh]">
							<div className="p-6">
								{previewUrl ? (
									<div className="space-y-6">
										<div className="text-center">
											<h3 className="text-lg font-medium text-center mb-4">
												Selected Image
											</h3>
											<div className="grid grid-cols-1 gap-6 max-w-xs mx-auto">
												<div className="relative">
													<img
														src={previewUrl}
														alt="Preview"
														className="w-full h-full object-cover aspect-square object-center transition-opacity duration-200 rounded-large border-2 border-primary/20"
													/>
												</div>
											</div>
											<div className="text-center mt-4">
												<p className="text-sm text-muted-foreground mb-4">
													This image will be uploaded when you save
												</p>
												<Button
													variant="primary"
													onClick={() => {
														setSelectedFile(null);
														setPreviewUrl(null);
														if (previewUrl) {
															URL.revokeObjectURL(previewUrl);
														}
													}}
												>
													<Icon name="X" className="w-4 h-4" />
													Choose Different Image
												</Button>
											</div>
										</div>
									</div>
								) : (
									<div className="space-y-6">
										{/* Upload new image section */}
										<div className="text-center">
											<div
												className="border-2 w-full border-dashed border-muted-foreground/25 rounded-2xl p-8 hover:border-primary/50 transition-colors cursor-pointer bg-muted/10 hover:bg-accent/50 inline-block"
												onClick={() => {
													// Trigger file input
													const fileInput = document.querySelector(
														'input[type="file"]',
													) as HTMLInputElement;
													fileInput?.click();
												}}
											>
												<div className="p-4 bg-primary/10 rounded-full inline-flex mb-4">
													<Icon name="Plus" className="w-8 h-8 text-primary" />
												</div>
												<div className="text-center">
													<p className="text-lg font-medium text-foreground mb-1">
														Upload New Image
													</p>
													<p className="text-sm text-muted-foreground">
														Click to select an image file
													</p>
												</div>
											</div>
										</div>

										{/* Recent images section */}
										{recentImages.length > 0 && (
											<div className="space-y-4">
												<h3 className="text-lg font-medium text-center">
													Recent Images
												</h3>
												<div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
													{recentImages.map((img) => (
														<div key={img.id} className="relative group">
															<div
																className="aspect-square rounded-lg overflow-hidden border-2 border-border hover:border-primary/50 transition-all cursor-pointer"
																onClick={() => {
																	setSelectedImageUrl(img.imageUrl);
																	// Clear file selection when category image is selected
																	setSelectedFile(null);
																	if (previewUrl) {
																		URL.revokeObjectURL(previewUrl);
																		setPreviewUrl(null);
																	}
																}}
															>
																<Image
																	src={img.imageUrl}
																	alt={img.name}
																	className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
																/>
															</div>

															{selectedImageUrl === img.imageUrl && (
																<div className="absolute inset-0 bg-black/40 flex items-center justify-center backdrop-blur-[1px] rounded-lg">
																	<div className="bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2 shadow-lg">
																		<Icon name="Check" className="w-4 h-4" />
																		Selected
																	</div>
																</div>
															)}

															<div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 rounded-b-lg">
																<p className="text-white text-xs font-medium truncate">
																	{img.name}
																</p>
															</div>
														</div>
													))}
												</div>
											</div>
										)}
									</div>
								)}

								{/* Hidden file input */}
								<input
									type="file"
									accept="image/*"
									className="hidden"
									onChange={handleFileSelect}
									disabled={uploading}
								/>
							</div>
						</ScrollArea>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
}
